import { createSelector, createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import requests from "../../api/requests"

/**
 * Subscription status (GET /v1/users/subscription/status), spec asaas-pix.
 *
 * data.status: 'active' | 'trialing' | 'past_due' | 'incomplete' | 'incomplete_expired' | 'unpaid' | 'canceled' | 'not_subscribed'
 * data.has_access: boolean — drives whether content is gated (past_due within grace = true)
 * data.provider: 'stripe' | 'asaas'
 * data.payment_method: 'card' | 'boleto' | 'pix_automatic' | 'pix'
 * data.current_period_end: ISO string · data.next_charge_date: 'YYYY-MM-DD'
 * data.cancel_at_period_end: boolean
 * data.grace_days_remaining: int, while a grace period is running
 * data.pending: { kind: 'pix' | 'card' | 'invoice', ref, due_date, invoice_url }
 *   invoice_url is a bearer secret — never log it nor persist it (this slice
 *   is in memory only and cleared on logout). Pix QRs come from
 *   POST /v1/users/subscription/pay, never from the status (D47).
 * data.migration: { required, deadline } — boleto members inside the migration window
 * data.switch: { to_pix_available, window_opens_at } — card/boleto → Pix (D13)
 * data.reauthorize: { available, window_opens_at } — monthly Pix → Pix Automático (D48)
 * data.plan: { interval: 'month' | 'year', amount } — amount in BRL
 * data.scheduled_plan: { interval, amount, effective_at } — plan from the next renewal (D69)
 * data.plan_change: { available } — "Mudar para anual/mensal" offered now
 */
export const fetchSubscriptionStatus = createAsyncThunk(
  "subscriptionStatus/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await requests.getSubscriptionStatus()
      return res.data
    } catch (err) {
      return rejectWithValue(err?.response?.status ?? "network_error")
    }
  }
)

const subscriptionStatusSlice = createSlice({
  name: "subscriptionStatus",
  initialState: {
    data: null,
    loading: false,
    error: null,
    lastFetchedAt: 0,
    // Requests started since the last clear: a response that lands after a
    // logout belongs to the previous member and is dropped.
    inFlight: {},
  },
  reducers: {
    clearSubscriptionStatus: (state) => {
      state.data = null
      state.error = null
      state.lastFetchedAt = 0
      state.loading = false
      state.inFlight = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionStatus.pending, (state, action) => {
        state.loading = true
        state.inFlight[action.meta.requestId] = true
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        if (!state.inFlight[action.meta.requestId]) return
        delete state.inFlight[action.meta.requestId]
        state.loading = Object.keys(state.inFlight).length > 0
        state.data = action.payload
        state.error = null
        state.lastFetchedAt = Date.now()
      })
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        if (!state.inFlight[action.meta.requestId]) return
        delete state.inFlight[action.meta.requestId]
        state.loading = Object.keys(state.inFlight).length > 0
        state.error = action.payload
      })
  },
})

export const { clearSubscriptionStatus } = subscriptionStatusSlice.actions

// Selectors ------------------------------------------------------------------

export const PENDING_KINDS = ["pix", "card", "invoice"]

// Statuses with nothing left to pay or recover: the member subscribes again.
export const RESUBSCRIBE_STATUSES = ["not_subscribed", "incomplete_expired", "canceled"]

// Statuses with a payment to settle (past_due out of grace, Stripe unpaid).
export const UNPAID_STATUSES = ["past_due", "unpaid"]

/**
 * Where a member without access goes, by plan status — the 403 routing and
 * every screen that bounces a member whose status changed: first payment
 * still open → /awaiting-payment; nothing to recover → /plan; anything else
 * (past_due out of grace, unpaid) → /unpaid.
 */
export const routeForStatus = (status) => {
  if (status === "incomplete") return "/awaiting-payment"
  if (RESUBSCRIBE_STATUSES.includes(status)) return "/plan"
  return "/unpaid"
}

export const selectSubscriptionStatus = (state) => state.subscriptionStatus.data
export const selectHasAccess = (state) =>
  state.subscriptionStatus.data?.has_access ?? false
export const selectSubscriptionRawStatus = (state) =>
  state.subscriptionStatus.data?.status ?? null
export const selectProvider = (state) =>
  state.subscriptionStatus.data?.provider ?? null

/**
 * The charge the member still has to pay, or null. `ref` (invoice / payment
 * id) is the only value safe to use as a key — never the URL or the QR.
 */
export const selectPending = (state) => {
  const d = state.subscriptionStatus.data
  const p = d?.pending
  if (!p?.ref || !PENDING_KINDS.includes(p.kind)) return null
  return {
    kind: p.kind,
    ref: p.ref,
    dueDate: p.due_date ?? null,
    invoiceUrl: p.invoice_url ?? null,
    graceDaysRemaining:
      typeof d.grace_days_remaining === "number" ? d.grace_days_remaining : null,
  }
}

export const selectMigration = (state) => {
  const m = state.subscriptionStatus.data?.migration
  if (!m?.required || !m.deadline) return null
  return { deadline: m.deadline }
}

export const selectSwitch = (state) => {
  const s = state.subscriptionStatus.data?.switch
  if (!s) return null
  return {
    toPixAvailable: Boolean(s.to_pix_available),
    windowOpensAt: s.window_opens_at ?? null,
  }
}

export const selectReauthorize = (state) => {
  const r = state.subscriptionStatus.data?.reauthorize
  if (!r) return null
  return {
    available: Boolean(r.available),
    windowOpensAt: r.window_opens_at ?? null,
  }
}

// Memoized: the status is polled, and a new object per call re-renders.
export const selectPlan = createSelector(
  (state) => state.subscriptionStatus.data?.plan,
  (p) => (p?.interval ? { interval: p.interval, amount: p.amount ?? null } : null)
)

export const selectScheduledPlan = createSelector(
  (state) => state.subscriptionStatus.data?.scheduled_plan,
  (p) =>
    p?.interval ? { interval: p.interval, amount: p.amount ?? null, effectiveAt: p.effective_at ?? null } : null
)

// Promotional trial (api D92): running while in trial; ended while a Pix
// trial's first charge is unpaid, still payable until payUntil (D85).
export const selectTrial = createSelector(
  (state) => state.subscriptionStatus.data?.trial,
  (t) =>
    t?.state === "running" || t?.state === "ended"
      ? {
          state: t.state,
          endsAt: t.ends_at ?? null,
          firstChargeDate: t.first_charge_date ?? null,
          payUntil: t.pay_until ?? null,
          amount: typeof t.amount === "number" ? t.amount : null,
        }
      : null
)

export const selectPlanChangeAvailable = (state) =>
  Boolean(state.subscriptionStatus.data?.plan_change?.available)

export default subscriptionStatusSlice.reducer
